// Tiny router. We don't need Vue/React Router — five routes, hash-based,
// fits in 50 lines. Two responsibilities:
//   - Map a hash to a (tab, optional topic slug) tuple.
//   - Provide a navigate() helper that updates the hash and notifies.
//
// Routes:
//   #/cockpit              — exec landing (default)
//   #/cluster              — cluster map
//   #/matrix               — magic-quadrant matrix
//   #/cloud                — word cloud
//   #/memos                — memo template gallery
//   #/sandbox              — Bayesian scenario engine
//   #/capture              — capture lane (URL · text · PDF)
//   #/console              — curator workbench (pending + runs + sources)
//   #/inbox, #/ops         — legacy aliases that route to #/console
//   #/admin                — taxonomy + people
//   #/topic/<slug>         — pinned single-topic view (share link)

export type Tab = "cockpit" | "cluster" | "matrix" | "cloud" | "memos" | "sandbox" | "capture" | "console" | "inbox" | "ops" | "admin" | "topic";

export interface Route {
  tab: Tab;
  topicSlug: string | null;
}

const DEFAULT: Route = { tab: "cockpit", topicSlug: null };

export function parse(hash: string = location.hash): Route {
  const h = hash.replace(/^#\/?/, "");
  if (!h) return DEFAULT;
  const [head, rest] = h.split("/");
  // Strip query string before tab matching — used by cockpit cell drill-ins
  // (e.g. #/cluster?macro=tech&lens=client).
  const headBase = head.split("?")[0];
  if (headBase === "topic" && rest) return { tab: "topic", topicSlug: rest.split("?")[0] };
  // Legacy aliases — old links to #/inbox or #/ops keep working.
  if (headBase === "inbox" || headBase === "ops") {
    return { tab: "console", topicSlug: null };
  }
  if (
    headBase === "cockpit" || headBase === "cluster" || headBase === "matrix" ||
    headBase === "cloud"   || headBase === "memos"   || headBase === "sandbox" ||
    headBase === "capture" || headBase === "console" || headBase === "admin"
  ) {
    return { tab: headBase as Tab, topicSlug: null };
  }
  return DEFAULT;
}

export function navigate(tab: Tab, topicSlug?: string): void {
  const target = tab === "topic" && topicSlug
    ? `#/topic/${encodeURIComponent(topicSlug)}`
    : `#/${tab}`;
  if (location.hash !== target) location.hash = target;
}

export function onRouteChange(cb: (r: Route) => void): () => void {
  const handler = () => cb(parse());
  window.addEventListener("hashchange", handler);
  return () => window.removeEventListener("hashchange", handler);
}
